/*
  Cloudinary already resizes, re-encodes and serves these images from its CDN,
  and the `srcSet` below picks the width. Routing them through `next/image`
  would put a second optimiser in front of that for no gain, so the plain
  element is deliberate.
*/
/* eslint-disable @next/next/no-img-element */
import { AdvancedVideo } from '@cloudinary/react';
import { dpr } from '@cloudinary/url-gen/actions/delivery';
import { fill, limitFit } from '@cloudinary/url-gen/actions/resize';
import { Icon, Text } from 'components/ui';
import { ListingMedia } from 'lib/api/generated';
import {
  CARD_IMAGE_WIDTHS,
  CAROUSEL_THUMBNAIL_SIZES,
  CAROUSEL_THUMBNAIL_WIDTHS,
  DETAIL_MEDIA_SIZES,
  buildResponsiveImage,
} from 'lib/cloudinary-image';
import { getMediaAltText } from 'lib/helpers';
import lightGallery from 'lightgallery';
import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-video.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lightgallery.css';
import { GalleryItem } from 'lightgallery/lg-utils';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgVideo from 'lightgallery/plugins/video';
import lgZoom from 'lightgallery/plugins/zoom';
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { CLOUDINARY_MAX_WIDTH, cld } from 'settings/config';
import styled from 'styled-components';
import { globalTheme } from 'styles/global-theme';

const CarouselContainer = styled.div`
  margin: 0 auto;
  border-radius: 8px;
  user-select: none;
`;

const StyledCarousel = styled(Carousel)`
  position: relative;
  background-color: transparent;
  .slide div img,
  .slide div video {
    max-height: 400px;
    height: 100%;
    width: 100%;
    object-fit: contain;
    border-radius: 4px;
    cursor: pointer;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }
  .thumbs-wrapper {
    margin: 5px !important;
    .control-arrow {
      display: none;
    }
  }

  .thumb {
    opacity: 0.7;
    border-radius: 4px;
    img {
      border-radius: 4px;
      width: 100% !important;
      object-fit: cover;
    }

    &.selected {
      opacity: 1;
      border-color: rgba(0, 0, 0, 0.5);
    }
  }

  .control-arrow {
    background: rgba(0, 0, 0, 0.3) !important;
    color: white;
  }
`;

const LightGalleryContainer = styled.div`
  //display: none;
`;
const FullScreenContainer = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.4);
  right: 10px;
  bottom: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: rgba(0, 0, 0, 1);
  }
`;

const PlayIconContainer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

/** Marks a video thumbnail; its parent needs to be positioned. */
const PlayIconOverlay = () => (
  <PlayIconContainer>
    <Icon component='PlayIcon' size={5} />
  </PlayIconContainer>
);

const CarousalArrow = styled.div`
  position: absolute;
  top: calc(50% - 15px);
  width: 30px;
  height: 30px;
  cursor: pointer;
  z-index: 3;
  background-color: #efefef;
  display: flex;
  border-radius: ${globalTheme.radii.rounded};
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 4px 1px rgba(0, 0, 0, 0.2);
`;

export const FullScreenImageViewer = ({
  images,
  altText,
}: {
  images: ListingMedia[];
  altText?: string;
}) => {
  const lightGalleryRef = useRef<any | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  // lightGallery's video thumbnails, which get the same play icon as the
  // carousel's through a portal.
  const [lightboxVideoThumbs, setLightboxVideoThumbs] = useState<HTMLElement[]>(
    []
  );
  const openLightGallery = (index: number) => {
    if (lightGalleryRef.current) {
      // Stop an inline carousel video so it doesn't keep playing behind the lightbox.
      carouselRef.current
        ?.querySelectorAll('video')
        .forEach((video) => video.pause());
      lightGalleryRef.current.openGallery(index);
    }
  };

  React.useEffect(() => {
    const lightGalleryElement = document.getElementById('lightgallery');
    if (!lightGalleryElement) return;

    lightGalleryRef.current = lightGallery(lightGalleryElement, {
      plugins: [lgThumbnail, lgZoom, lgVideo],
      download: true,
      dynamic: true,
      speed: 500,
      // One gallery item per carousel slide, in the same order, so the slide
      // index can be passed straight to openGallery.
      dynamicEl: images.map(toGalleryItem),
      gotoNextSlideOnVideoEnd: false,
      mobileSettings: {
        showCloseIcon: true,
      },
    });

    // lightGallery blocks clicks on a disabled download link but leaves it
    // focusable with the previous slide's href, so take it out of the tab
    // order too. This fires after lightGallery has set the download state.
    const syncDownloadLink = (event: Event) => {
      const { index } = (event as CustomEvent<{ index: number }>).detail;
      const downloadLink = lightGalleryRef.current?.outer
        .get()
        ?.querySelector('.lg-download');
      if (!downloadLink) return;
      if (images[index]?.resource_type === 'video') {
        downloadLink.setAttribute('aria-disabled', 'true');
        downloadLink.setAttribute('tabindex', '-1');
      } else {
        downloadLink.removeAttribute('aria-disabled');
        downloadLink.removeAttribute('tabindex');
      }
    };
    lightGalleryElement.addEventListener('lgBeforeSlide', syncDownloadLink);

    // The thumbnail plugin renders every item as a bare <img>, so find the
    // video ones to portal the play icon into.
    const findVideoThumbs = () => {
      const thumbs: HTMLElement[] = Array.from(
        lightGalleryRef.current?.outer
          .get()
          ?.querySelectorAll('.lg-thumb-item') ?? []
      );
      const videoThumbs = thumbs.filter(
        (thumb) =>
          images[Number(thumb.dataset.lgItemId)]?.resource_type === 'video'
      );
      videoThumbs.forEach((thumb) => (thumb.style.position = 'relative'));
      setLightboxVideoThumbs(videoThumbs);
    };
    lightGalleryElement.addEventListener('lgAfterOpen', findVideoThumbs);

    return () => {
      lightGalleryElement.removeEventListener(
        'lgBeforeSlide',
        syncDownloadLink
      );
      lightGalleryElement.removeEventListener('lgAfterOpen', findVideoThumbs);
      setLightboxVideoThumbs([]);
      if (lightGalleryRef.current) {
        lightGalleryRef.current.destroy();
        lightGalleryRef.current = null;
      }
    };
  }, [images]);

  return (
    <CarouselContainer ref={carouselRef}>
      <LightGalleryContainer id='lightgallery' />
      {lightboxVideoThumbs.map((thumb) =>
        createPortal(<PlayIconOverlay />, thumb, thumb.dataset.lgItemId)
      )}
      <StyledCarousel
        showArrows={true}
        showStatus={true}
        showIndicators={false}
        showThumbs={images.length > 1}
        infiniteLoop={true}
        useKeyboardArrows={true}
        autoPlay={false}
        stopOnHover={true}
        dynamicHeight={false}
        emulateTouch={true}
        thumbWidth={100}
        renderThumbs={() =>
          images.map((image: ListingMedia, index: number) => {
            const videoPoster = buildResponsiveImage(
              (width) =>
                cld
                  .video(image?.public_id)
                  .format('jpg')
                  .resize(fill().aspectRatio('3:2').width(width)),
              CAROUSEL_THUMBNAIL_WIDTHS
            );
            return image.resource_type === 'image' ? (
              <div key={index} style={{ height: '100%' }}>
                <CarouselItem
                  media={image}
                  altText={altText}
                  variant='thumbnail'
                />
              </div>
            ) : (
              <div
                key={index}
                style={{
                  position: 'relative',
                  maxHeight: '100px',
                  height: '100%',
                }}
              >
                <img
                  {...videoPoster}
                  sizes={CAROUSEL_THUMBNAIL_SIZES}
                  alt={getMediaAltText(image, altText || 'Listing photo')}
                />
                <PlayIconOverlay />
              </div>
            );
          })
        }
        renderArrowPrev={(onClickHandler, hasPrev, label) =>
          hasPrev && (
            <CarousalArrow onClick={onClickHandler} style={{ left: 15 }}>
              <Icon component='ArrowLeftIcon' size={'29px'} />
            </CarousalArrow>
          )
        }
        renderArrowNext={(onClickHandler, hasNext, label) =>
          hasNext && (
            <CarousalArrow onClick={onClickHandler} style={{ right: 15 }}>
              <Icon component='ArrowRightIcon' />
            </CarousalArrow>
          )
        }
      >
        {images.map((image: ListingMedia, index: number) => (
          <div style={{ height: '100%' }} key={index}>
            <CarouselItem media={image} altText={altText} />
            {/* Videos already have a fullscreen control in their player. */}
            {image?.resource_type === 'image' && (
              <FullScreenContainer onClick={() => openLightGallery(index)}>
                <Icon component='MaximizeIcon' size={2} color='white' />
                <Text as={'span'} color='white'>
                  Fullscreen
                </Text>
              </FullScreenContainer>
            )}
          </div>
        ))}
      </StyledCarousel>
    </CarouselContainer>
  );
};

const toGalleryItem = (media: ListingMedia): GalleryItem => {
  if (media.resource_type === 'video') {
    const poster = (width: number) =>
      cld
        .video(media.public_id)
        .format('jpg')
        .delivery(dpr(2))
        .resize(limitFit().width(width))
        .toURL();
    return {
      // Uploads are often .mov, so ask Cloudinary for an mp4 every browser plays.
      video: {
        source: [
          {
            src: cld
              .video(media.public_id)
              .format('mp4')
              .quality('auto')
              .resize(limitFit().width(CLOUDINARY_MAX_WIDTH.fullScreen))
              .toURL(),
            type: 'video/mp4',
          },
        ],
        tracks: [],
        attributes: {
          preload: 'metadata',
          controls: true,
          playsinline: true,
          poster: poster(CLOUDINARY_MAX_WIDTH.fullScreen),
        } as unknown as HTMLVideoElement,
      },
      thumb: poster(CLOUDINARY_MAX_WIDTH.thumbnail),
      // Videos aren't downloadable: false greys out and disables the button.
      downloadUrl: false,
    };
  }

  return {
    src: cld
      .image(media.public_id)
      .format('auto')
      .quality('auto')
      .delivery(dpr(2))
      .resize(limitFit().width(CLOUDINARY_MAX_WIDTH.fullScreen))
      .toURL(),
    thumb: cld
      .image(media.public_id)
      .format('auto')
      .quality('auto')
      .delivery(dpr(2))
      .resize(limitFit().width(CLOUDINARY_MAX_WIDTH.thumbnail))
      .toURL(),
    // The download button should still hand over the untouched upload.
    downloadUrl: `https://res.cloudinary.com/${process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME}/image/upload/${media.public_id}.${media.format}`,
  };
};

const CarouselItem = ({
  media,
  altText,
  variant = 'slide',
}: {
  media: ListingMedia;
  altText?: string;
  /**
   * The same item renders both the ~400px carousel slide and the 100px
   * thumbnail strip, so the width it asks Cloudinary for has to follow.
   */
  variant?: 'slide' | 'thumbnail';
}) => {
  const isThumbnail = variant === 'thumbnail';
  const widths = isThumbnail ? CAROUSEL_THUMBNAIL_WIDTHS : CARD_IMAGE_WIDTHS;

  const image = buildResponsiveImage(
    (width) =>
      cld
        .image(media.public_id)
        .format('auto')
        .quality('auto')
        .resize(limitFit().width(width)),
    widths
  );
  const cloudVideo = cld.video(media.public_id);

  const cloudVideoPoster = cld
    .video(media?.public_id)
    .format('jpg')
    .resize(
      fill()
        .aspectRatio('3:2')
        .width(widths[widths.length - 1])
    );
  return media?.resource_type === 'image' ? (
    <img
      {...image}
      sizes={isThumbnail ? CAROUSEL_THUMBNAIL_SIZES : DETAIL_MEDIA_SIZES}
      alt={getMediaAltText(media, altText || 'Listing photo')}
    />
  ) : (
    <AdvancedVideo
      cldVid={cloudVideo}
      cldPoster={cloudVideoPoster}
      controls
      playsInline
    />
  );
};
